import { SPAN } from './constants';
import { cheb, cx, cy, wireLength } from './geometry';
import type { Point, Puzzle, ScoredPuzzle, Stats, Tier } from './types';

/**
 * The wire floor: an MST over plant + houses using straight-line distance,
 * ignoring the span rule entirely.
 *
 * Retired from the player-facing score (GDD §3.4) but load-bearing where it
 * remains: through {@link mwFor} it is the solver-independent yardstick of
 * the generator's headroom probe (GDD §5.3) — re-denominating that probe in
 * reference wire would couple generation to the search, which §5.5 forbids.
 */
export function mstFloor(puzzle: Puzzle): number {
  const T = [puzzle.plant, ...puzzle.houses];
  const inTree = [0];
  const dist = T.map((t, i) => (i ? Math.hypot(cx(t) - cx(T[0]), cy(t) - cy(T[0])) : 0));
  let total = 0;
  while (inTree.length < T.length) {
    let bi = -1;
    let bd = 1e18;
    for (let i = 0; i < T.length; i++) {
      if (!inTree.includes(i) && dist[i] < bd) {
        bd = dist[i];
        bi = i;
      }
    }
    total += bd;
    inTree.push(bi);
    for (let i = 0; i < T.length; i++) {
      if (!inTree.includes(i)) {
        dist[i] = Math.min(dist[i], Math.hypot(cx(T[i]) - cx(T[bi]), cy(T[i]) - cy(T[bi])));
      }
    }
  }
  return total;
}

/**
 * The same tree {@link mstFloor} measures, as the edges it is made of.
 *
 * Not drawn anywhere: it is geometry, not a layout, and it was briefly
 * rendered on the board before that was cut for teaching the wrong thing (a
 * line between two houses is a connection the span rule forbids). What reads
 * it now is the topology classifier, which measures how close a reference's
 * crossing sits to this tree.
 *
 * Deliberately a second pass rather than a refactor of {@link mstFloor} to
 * return both: that function's floating-point summation order is baked into
 * every MW score, and a tidier shared implementation that summed in a
 * different order would move scores by a hair across the whole archive.
 * Duplication is the cheaper of the two prices.
 */
export function mstEdges(puzzle: Puzzle): [Point, Point][] {
  const T = [puzzle.plant, ...puzzle.houses];
  const inTree = [0];
  const dist = T.map((t, i) => (i ? Math.hypot(cx(t) - cx(T[0]), cy(t) - cy(T[0])) : 0));
  const from = T.map(() => 0);
  const edges: [Point, Point][] = [];

  while (inTree.length < T.length) {
    let bi = -1;
    let bd = 1e18;
    for (let i = 0; i < T.length; i++) {
      if (!inTree.includes(i) && dist[i] < bd) {
        bd = dist[i];
        bi = i;
      }
    }
    edges.push([T[from[bi]], T[bi]]);
    inTree.push(bi);
    for (let i = 0; i < T.length; i++) {
      if (!inTree.includes(i)) {
        const d = Math.hypot(cx(T[i]) - cx(T[bi]), cy(T[i]) - cy(T[bi]));
        if (d < dist[i]) {
          dist[i] = d;
          from[i] = bi;
        }
      }
    }
  }
  return edges;
}

/**
 * Physical accounting for a tower layout.
 *
 * This MUST mirror the renderer's rules — see `network()`. Every node within
 * span of an energized node is itself energized; every such pair strings wire;
 * each house takes its single best drop, preferring a live source and then the
 * nearest one.
 */
export function statsFor(puzzle: Puzzle, towers: Point[]): Stats {
  const nodes = [puzzle.plant, ...towers];
  const energized = new Set([0]);
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 1; i < nodes.length; i++) {
      if (energized.has(i)) continue;
      for (const j of energized) {
        if (cheb(nodes[i], nodes[j]) <= SPAN) {
          energized.add(i);
          changed = true;
          break;
        }
      }
    }
  }

  let wire = 0;
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      if (cheb(nodes[i], nodes[j]) <= SPAN) wire += wireLength(nodes[i], nodes[j]);
    }
  }

  let lit = 0;
  for (const h of puzzle.houses) {
    let best: Point | null = null;
    let bestD = 1e9;
    let isLit = false;
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      if (cheb(n, h) <= SPAN) {
        const d = (n.r - h.r) ** 2 + (n.c - h.c) ** 2;
        const e = energized.has(i);
        if (e && !isLit) {
          isLit = true;
          best = n;
          bestD = d;
        } else if (e === isLit && d < bestD) {
          best = n;
          bestD = d;
        }
      }
    }
    if (best) wire += wireLength(best, h);
    if (isLit) lit++;
  }

  return { ok: lit === puzzle.houses.length, lit, wire };
}

/**
 * The floor-normalized read: the wire floor as a percentage of wire strung.
 *
 * Never shown to a player — the score is {@link refMWFor}. This survives as
 * the internal unit of the generator's headroom probe and the sim suite's
 * curation studies (GDD §5.3), where its independence from the solver is
 * load-bearing. The clamp at 100 absorbs Steiner layouts dipping under the
 * floor.
 */
export function mwFor(puzzle: ScoredPuzzle, wire: number): number {
  return Math.max(1, Math.min(100, Math.round((100 * puzzle.mst) / wire)));
}

/**
 * The precision band's lower edge, in MW. On and above it a reading prints to
 * one decimal ({@link refMWFor}); below it, whole MW. Precision follows the
 * integer, not the medal: whole numbers go numb here, and this edge stays put
 * when the tier cuts move, because a printed reading must never change with a
 * re-banding (GDD §3.4).
 */
export const PRECISION_BAND_MW = 96;

/** Matching the reference within 0.1% — the perfect grid, and 🏆. */
export const PERFECT_CUT = 0.999;

/**
 * 🥇 begins here — practically perfect execution. On a 9×9 board the
 * achievable scores pack the top so tightly that the old 0.96 cut was one
 * casual nudge off the reference on most days (GDD §9, the lattice study);
 * 0.98 keeps the medal meaning what it says.
 */
export const MASTER_CUT = 0.98;

/** 🥈 begins here — a completed grid that has been genuinely tuned. */
export const JOURNEYMAN_CUT = 0.93;

/**
 * The score (GDD §3.4): delivery as a share of the reference grid's own
 * 100 MW. Floored at 1 and open above 100 — beating the engineers reads as
 * more than the plant's rating, because it is.
 *
 * Quantized at the source to what the readouts may print, truncating rather
 * than rounding: a displayed 96 is a reached 96 (Pillar 4), and the tier cuts
 * of GDD §3.5 sit on printable values (98.0, 93), so the bands read exactly
 * off the printed number. Whole MW below the precision band, one decimal
 * inside it — the decimal is the distance to the reference, so precision
 * appears exactly where the integer goes numb, whichever medal a reading
 * earns.
 */
export function refMWFor(refWire: number, wire: number): number {
  // The epsilon shields quantization from float noise on exact thresholds
  // (a true 0.96 ratio must not truncate to 95, nor a true 0.98 print as
  // 97.9); it is far below anything a distinct layout could produce.
  const raw = Math.max(1, (100 * refWire) / wire + 1e-9);
  return raw >= PRECISION_BAND_MW ? Math.floor(raw * 10) / 10 : Math.floor(raw);
}

/** A reading as the readouts print it: whole MW, one decimal inside the precision band. */
export function mwLabel(mw: number): string {
  return Number.isInteger(mw) ? String(mw) : mw.toFixed(1);
}

/** How many bands the megawatt scale is read in for a distribution. */
export const MW_BUCKETS = 10;

/**
 * Which tenth of the scale a reading falls in, 0 through 9.
 *
 * Defined once and shared by the field readout's own histogram and the count
 * it submits, because two copies of this mapper desynchronise the moment one
 * of them is adjusted. Band 0 is 1-10 and band 9 is 91 up — a reading above
 * 100 ({@link refMWFor} is open at the top) counts in the top band.
 */
export function mwBucket(mw: number): number {
  return Math.max(0, Math.min(MW_BUCKETS - 1, Math.floor((mw - 1) / 10)));
}

/**
 * Rank a layout against the best known grid.
 *
 * Returns the medal and a stable rank key, never a word: the words are copy
 * and live in `ui/strings.ts`, while this key is what storage and the usage
 * stream file the result under, in every language.
 */
export function tierFor(refWire: number, wire: number): Tier {
  const r = refWire / wire;
  return r >= PERFECT_CUT
    ? { emoji: '🏆', rank: 'superconductor' }
    : r >= MASTER_CUT
      ? { emoji: '🥇', rank: 'master' }
      : r >= JOURNEYMAN_CUT
        ? { emoji: '🥈', rank: 'journeyman' }
        : { emoji: '🥉', rank: 'apprentice' };
}
